import React from 'react';
import { DeviceTypeChip, Callout, FactRow, Mono, EmptyState } from './RmaBits';
import { warrantyShort, warrantyEnd, TONE, recordedFault, stageOf, OPEN_STAGES } from './rmaHelpers';
import { stripSerial } from '../../utils/scan';

// Screen 3 — raise an RMA. Three steps: describe the fault, scan the units, book the
// shipment.
//
// THE WHOLE DRAFT IS LOCAL STATE. Nothing here calls setSt except the two commit buttons
// themselves. That is the point of this component: the old screen wrote the entire
// state blob to Azure on every keystroke of a fault description.

const SCAN_COLS = '24px 118px 60px minmax(0,1fr) 128px 78px';

// Source labels for a fault read off the device record, keyed by recordedFault().sourceKey.
const FAULT_SOURCE_TONE = { qc: 'info', quarantine: 'warning', maintenance: 'brand' };

// Space below the last card. The Shipment step is the final thing on the page and its
// Carrier menu opens downward, so without this it is pressed against the viewport edge
// with nowhere to scroll to. (The date panel opens upward and does not need this.)
const FOOT_ROOM = 220;

// `clip` is opt-out because a card containing a dropdown must not crop it. The clipping is
// only there to keep full-bleed rows (the scanned-units table) inside the rounded corners,
// and the Shipment step has none — so it opts out and its Carrier menu can overhang.
function StepCard({ n, title, sub, right, children, clip = true }) {
  return (
    <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', ...(clip ? { overflow: 'hidden' } : {}) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border-default)' }}>
        <span style={{
          width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-default)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, flexShrink: 0,
        }}>{n}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
          {sub && <div style={{ fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 2 }}>{sub}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

const ScanBox = ({ value, onChange, onKeyDown, placeholder, inputRef }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface-default)',
    border: '1.5px solid var(--brand-default)', borderRadius: 'var(--radius-lg)',
    padding: '0 14px', height: 46, boxShadow: '0 0 0 3px var(--brand-tint)',
  }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-default)" strokeWidth="2" strokeLinecap="round">
      <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2M7 8v8M11 8v8M15 8v8M18 8v8" />
    </svg>
    <input
      ref={inputRef}
      value={value} onChange={onChange} onKeyDown={onKeyDown} placeholder={placeholder}
      style={{
        flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14,
        color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', minWidth: 0,
      }}
    />
    <span style={{ fontSize: 11, color: 'var(--text-ghost)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>Enter</span>
  </div>
);

function RmaNew({
  st, faultCategories, isReadOnly,
  onBack, onCreate,
  editing,
  initialDeviceIds, initialFaultCategory, initialFaultText,
  FilterSelect, DatePicker, darkMode, OW, DM,
  buzzSuccess, buzzError,
  now = Date.now(),
}) {
  const settings = st.rmaSettings;
  const months = settings?.warrantyMonths;
  // Memoised so the `|| []` fallback cannot invalidate the memos below on every render.
  const devices = React.useMemo(() => st.devices || [], [st.devices]);
  const devicesById = React.useMemo(() => new Map(devices.map(d => [d.id, d])), [devices]);

  const [faultMode, setFaultMode] = React.useState(editing?.faultMode || 'single');
  const [faultCategory, setFaultCategory] = React.useState(initialFaultCategory || '');
  const [faultText, setFaultText] = React.useState(initialFaultText || '');
  const [unitIds, setUnitIds] = React.useState(initialDeviceIds || []);
  // Re-opening a saved RMA rehydrates what was filed for each unit — both the category and
  // the verbatim text — so an edit starts from what is on the record rather than from blank.
  const [perDeviceFaults, setPerDeviceFaults] = React.useState(() => {
    if (!editing) return {};
    const out = {};
    Object.entries(editing.faultCategories || {}).forEach(([id, v]) => { if (v) out[id] = v; });
    Object.entries(editing.faultDescriptions || {}).forEach(([id, v]) => { if (v) out[id + ':text'] = v; });
    return out;
  });
  const [scanInput, setScanInput] = React.useState('');
  const [scanError, setScanError] = React.useState('');
  const [carrier, setCarrier] = React.useState(editing?.carrier || '');
  const [tracking, setTracking] = React.useState(editing?.trackingNumber || '');
  const [dispatchDate, setDispatchDate] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const scanRef = React.useRef(null);

  const units = unitIds.map(id => devicesById.get(id)).filter(Boolean);

  const outOfWarranty = units.filter(d => {
    const left = warrantyEnd(d.purchaseDate, months);
    return left && left.getTime() < now;
  });
  const unknownWarranty = units.filter(d => !d.purchaseDate);

  // ── Recorded faults ──
  // A unit scanned here has usually already been diagnosed — by goods-inwards QC, by the
  // bench test that quarantined it, or by the tech on its open maintenance job. Read that
  // fault back off the device so the operator does not re-pick (and possibly contradict)
  // a category the bench already decided.
  //
  // Keyed by device id, resolved once per unit set rather than per row, because the
  // maintenance-job scan is O(jobs) per device.
  const maintenanceJobs = React.useMemo(() => st.maintenanceJobs || [], [st.maintenanceJobs]);
  const recordedFaults = React.useMemo(() => {
    const out = {};
    units.forEach(d => {
      const f = recordedFault(d, { categories: faultCategories, maintenanceJobs });
      if (f) out[d.id] = f;
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitIds, devicesById, faultCategories, maintenanceJobs]);

  // Units the operator must still categorise by hand: no usable category on record. A unit
  // whose record holds only a note counts as uncovered — the note prefills the description,
  // but it cannot choose a chip.
  const uncovered = units.filter(d => !recordedFaults[d.id]?.category);
  // True once at least one unit carries its own recorded fault. This is what flips the
  // screen out of "one fault for all" framing: with mixed sources a single batch category
  // would be wrong for somebody.
  const anyRecorded = units.some(d => recordedFaults[d.id]?.category);

  // The category that will actually be filed for a unit. Recorded faults win over the
  // Step 1 chip; the chip is the fallback for units with nothing on record. In per-device
  // mode an explicit operator override wins over both — they are looking at the row.
  const effectiveCategory = (d) => {
    if (faultMode === 'per_device') return perDeviceFaults[d.id] || recordedFaults[d.id]?.category || '';
    return recordedFaults[d.id]?.category || faultCategory;
  };

  // Prefilled verbatim text. The batch textarea still applies to units with no record of
  // their own, so a mixed batch gets each unit's own words where they exist and the shared
  // description everywhere else.
  const effectiveDescription = (d) => {
    if (faultMode === 'per_device' && perDeviceFaults[d.id + ':text']) return perDeviceFaults[d.id + ':text'];
    return recordedFaults[d.id]?.description || faultText.trim();
  };

  // What the Fault row in the summary should read. Distinct categories are named while
  // there are few enough to be useful; past that a count is more honest than a list.
  const summaryFault = (() => {
    if (!units.length) return { text: faultCategory || 'not set', unset: !faultCategory };
    const distinct = [...new Set(units.map(effectiveCategory).filter(Boolean))];
    if (!distinct.length) return { text: 'not set', unset: true };
    if (distinct.length === 1) return { text: distinct[0], unset: false };
    if (distinct.length <= 3) return { text: distinct.join(', '), unset: false };
    return { text: distinct.length + ' different faults', unset: false };
  })();

  // Suggest other quarantined units carrying the same fault — the reason the category
  // vocabulary is derived from the QC checklist labels rather than invented here.
  const suggestions = React.useMemo(() => {
    if (!faultCategory || faultCategory === 'Other') return [];
    const want = faultCategory.toLowerCase();
    return devices.filter(d =>
      !unitIds.includes(d.id) &&
      (d.status === 'Quarantine' || d.status === 'Pending RMA') &&
      (d.quarantineFailedChecks || []).some(c => String(c).toLowerCase() === want));
  }, [devices, unitIds, faultCategory]);

  // Units already committed to another open RMA, so a scan cannot quietly poach one off a
  // return someone else is building. The RMA being edited is excluded — its own units are
  // supposed to be here.
  const claimedElsewhere = React.useMemo(() => {
    const out = new Map();
    (st.rmaShipments || []).forEach(sh => {
      if (editing && sh.id === editing.id) return;
      if (!OPEN_STAGES.includes(stageOf(sh))) return;
      (sh.deviceIds || []).forEach(id => out.set(id, sh.ref || 'another RMA'));
    });
    return out;
  }, [st.rmaShipments, editing]);

  const addSerial = (raw) => {
    const clean = stripSerial(raw);
    if (!clean) return;
    const dev = devices.find(d => String(d.serial).toLowerCase() === clean.toLowerCase());
    if (!dev) { setScanError('Serial not found: ' + clean); buzzError && buzzError(); return; }
    if (unitIds.includes(dev.id)) { setScanError(clean + ' is already on this RMA'); buzzError && buzzError(); return; }
    if (dev.status === 'Retired') { setScanError(clean + ' is retired and cannot be returned'); buzzError && buzzError(); return; }
    if (dev.status === 'With Manufacturer') { setScanError(clean + ' is already with the manufacturer'); buzzError && buzzError(); return; }
    if (claimedElsewhere.has(dev.id)) { setScanError(clean + ' is already on ' + claimedElsewhere.get(dev.id)); buzzError && buzzError(); return; }
    setUnitIds(ids => [...ids, dev.id]);
    setScanError('');
    buzzSuccess && buzzSuccess();
  };

  // Every unit must end up with a category, from whichever source. A recorded fault
  // satisfies its own unit, so the Step 1 chip is only required when something is still
  // uncovered — that is what removes the click when the bench already diagnosed the batch.
  const canCreate = units.length > 0 && !isReadOnly && !submitting &&
    units.every(d => !!effectiveCategory(d));

  const submit = (dispatchNow) => {
    if (!canCreate) return;
    setSubmitting(true);
    const faultDescriptions = {};
    units.forEach(d => { faultDescriptions[d.id] = effectiveDescription(d); });

    // Per-device categories are filed whenever the units do not all agree — which is the
    // normal case once faults come off individual records. `single` is kept only when one
    // category genuinely covers the whole batch, so the queue and detail screens can still
    // show "Buttons" rather than "Mixed faults" for a batch that really is uniform.
    const cats = {};
    units.forEach(d => { cats[d.id] = effectiveCategory(d); });
    const distinct = [...new Set(Object.values(cats))];
    const uniform = faultMode === 'single' && distinct.length === 1;

    onCreate({
      deviceIds: unitIds,
      faultMode: uniform ? 'single' : 'per_device',
      faultCategory: uniform ? distinct[0] : '',
      faultCategories: uniform ? {} : cats,
      faultDescriptions,
      // On create, carrier/tracking only mean anything if the parcel is going now. On an
      // edit they are part of the saved RMA, so whatever is in the form is kept — otherwise
      // re-saving would silently wipe a tracking number added earlier.
      carrier: (dispatchNow || editing) ? carrier : '',
      trackingNumber: (dispatchNow || editing) ? tracking.trim() : '',
      dispatchNow,
    });
  };

  const segStyle = (on) => ({
    padding: '5px 11px', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', lineHeight: 1, whiteSpace: 'nowrap',
    border: '1px solid ' + (on ? 'var(--border-hover)' : 'transparent'),
    background: on ? 'var(--surface-default)' : 'transparent',
    color: on ? 'var(--text-primary)' : 'var(--text-subtle)',
  });
  const fieldStyle = {
    display: 'flex', alignItems: 'center', height: 36, padding: '0 12px',
    background: 'var(--surface-default)', border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)', fontSize: 13, color: 'var(--text-default)', outline: 'none', width: '100%',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: FOOT_ROOM }}>
      <div>
        <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          {editing ? 'Back to ' + (editing.ref || 'RMA') : 'All RMAs'}
        </button>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>
          {editing ? 'Edit ' + (editing.ref || 'RMA') : 'New RMA'}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 4 }}>
          {editing
            ? 'Saved but not yet dispatched — add or remove units and adjust the fault. Nothing ships until you dispatch it.'
            : 'Three steps: describe the fault, scan the units, book the shipment.'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(328px,22%)', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Step 1 — fault */}
          <StepCard
            n={1} title="Fault"
            sub={anyRecorded
              ? (uncovered.length === 0
                  ? 'Every scanned unit already has a fault on record — nothing to pick here.'
                  : 'Applies to the ' + uncovered.length + ' scanned unit' + (uncovered.length === 1 ? '' : 's')
                    + ' with nothing on record. The rest keep their own recorded fault.')
              : 'Applies to every unit you scan. Switch to per-device if they differ.'}
            right={
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 3 }}>
                <button onClick={() => setFaultMode('single')} style={segStyle(faultMode === 'single')}>One fault for all</button>
                <button onClick={() => setFaultMode('per_device')} style={segStyle(faultMode === 'per_device')}>Per device</button>
              </div>
            }
          >
            <div style={{ padding: '16px 18px 18px' }}>
              {/* What the scan already resolved. Without this the operator cannot tell
                  whether an empty chip row is a step they skipped or a step they no longer
                  need — the whole point of reading faults off the record. */}
              {anyRecorded && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14, padding: '10px 13px',
                  background: uncovered.length ? 'var(--warning-fill)' : 'var(--success-fill)',
                  border: '1px solid ' + (uncovered.length ? 'var(--warning-border)' : 'var(--success-border)'),
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                       stroke={uncovered.length ? 'var(--warning-text)' : 'var(--success-text)'} style={{ flexShrink: 0, marginTop: 1 }}>
                    {uncovered.length
                      ? <><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></>
                      : <path d="M20 6L9 17l-5-5" />}
                  </svg>
                  <div style={{ fontSize: 12.5, lineHeight: 1.5, color: uncovered.length ? 'var(--warning-text)' : 'var(--success-text)' }}>
                    {units.length - uncovered.length} of {units.length} unit{units.length === 1 ? '' : 's'} came in
                    with a fault already recorded by QC, quarantine or maintenance — those are used as-is.
                    {uncovered.length > 0 && ' Describe the fault below for the remaining '
                      + uncovered.length + ' (' + uncovered.map(d => d.serial).slice(0, 3).join(', ')
                      + (uncovered.length > 3 ? ', +' + (uncovered.length - 3) + ' more' : '') + ').'}
                    {uncovered.length === 0 && ' Switch to Per device if you need to change any of them.'}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Fault <span style={{ color: 'var(--text-ghost)', fontWeight: 400 }}>
                  — a short summary in your own words
                </span>
              </div>
              {/* Free text rather than a fixed list: the operator usually knows more about
                  the fault than any category captures, and a wrong-but-nearby chip loses
                  that. Recorded faults still arrive as text from the QC/quarantine record,
                  so a typed fault and an inherited one are the same kind of thing. */}
              <input
                value={faultCategory}
                onChange={e => setFaultCategory(e.target.value)}
                placeholder="e.g. Buttons unresponsive after charge"
                style={{ ...fieldStyle, marginBottom: 16 }}
              />

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                What happens <span style={{ color: 'var(--text-ghost)', fontWeight: 400 }}>
                  — goes to {settings?.manufacturer || 'the manufacturer'} verbatim
                  {anyRecorded && ', for units with no note of their own'}
                </span>
              </div>
              {/* Local state: this must never touch setSt on keystroke. */}
              <textarea
                value={faultText}
                onChange={e => setFaultText(e.target.value)}
                rows={3}
                placeholder={anyRecorded && uncovered.length === 0
                  ? 'Optional — every unit already carries the note recorded when it failed.'
                  : 'Describe what the unit does, in the words you want the manufacturer to read.'}
                style={{
                  width: '100%', background: 'var(--surface-default)', border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-lg)', padding: '11px 13px', fontSize: 13,
                  color: 'var(--text-default)', lineHeight: 1.5, outline: 'none', resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          </StepCard>

          {/* Step 2 — units */}
          <StepCard
            n={2} title="Units"
            sub="Scan a barcode or type a serial and press Enter."
            right={
              <span className={'pill ' + (units.length ? 'pill-success' : 'pill-neutral')}>
                {units.length} unit{units.length === 1 ? '' : 's'}
              </span>
            }
          >
            <div style={{ padding: '16px 18px', background: 'var(--surface-raised)', borderBottom: '1px solid var(--border-default)' }}>
              <ScanBox
                inputRef={scanRef}
                value={scanInput}
                onChange={e => setScanInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  const v = scanInput.trim();
                  setScanInput('');
                  if (v) addSerial(v);
                }}
                placeholder="Scan or type serial…"
              />
              {scanError && (
                <div style={{ fontSize: 11.5, color: 'var(--danger-text)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
                  {scanError}
                </div>
              )}
              {suggestions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, padding: '10px 13px', background: 'var(--brand-tint)', border: '1px solid var(--brand-border)', borderRadius: 'var(--radius-lg)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--brand-light)" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
                  <div style={{ flex: 1, fontSize: 12.5, color: 'var(--brand-light)', lineHeight: 1.45 }}>
                    {suggestions.length} other quarantined unit{suggestions.length === 1 ? '' : 's'}{' '}
                    {suggestions.length === 1 ? 'has' : 'have'} the same {faultCategory.toLowerCase()} fault recorded on the bench.
                  </div>
                  <button
                    onClick={() => setUnitIds(ids => [...ids, ...suggestions.map(s => s.id)])}
                    className="btn btn-sm btn-primary" style={{ flexShrink: 0 }}
                  >Add all</button>
                </div>
              )}
            </div>

            {units.length === 0 && (
              <EmptyState
                title="No units yet"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 8v8M11 8v8M15 8v8M18 8v8" /></svg>}
              >
                Scan the first unit to start the batch. Every unit inherits the fault above.
              </EmptyState>
            )}

            {units.map(d => {
              const w = warrantyShort(d.purchaseDate, months, now);
              const wTone = TONE[w.tone] || TONE.neutral;
              const rec = recordedFaults[d.id];
              const recTone = TONE[FAULT_SOURCE_TONE[rec?.sourceKey]] || TONE.neutral;
              // In per-device mode the operator can override a recorded fault; the select
              // shows the recorded category as its value so overriding is a deliberate act
              // rather than a blank they have to fill in again.
              const shown = effectiveCategory(d);
              return (
                <div key={d.id} style={{ display: 'grid', gridTemplateColumns: SCAN_COLS, gap: 12, padding: '11px 18px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--success-icon)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  </span>
                  <Mono>{d.serial}</Mono>
                  <div><DeviceTypeChip type={d.type} /></div>
                  <div style={{ minWidth: 0 }}>
                    {faultMode === 'single' ? (
                      rec?.category ? (
                        <>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-default)' }}>{rec.category}</div>
                          <div style={{ fontSize: 11, color: recTone.text, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                               title={rec.checks.join(', ') + (rec.note ? ' — ' + rec.note : '')}>
                            from {rec.sourceLabel}
                            {rec.checks.length > 1 && ' · +' + (rec.checks.length - 1) + ' more'}
                          </div>
                        </>
                      ) : (
                        <span style={{ fontSize: 12.5, color: faultCategory ? 'var(--text-muted)' : 'var(--warning-text)' }}>
                          {faultCategory
                            ? faultCategory + ' — inherited from batch'
                            : (rec ? 'nothing on record — describe the fault above' : 'describe the fault above')}
                        </span>
                      )
                    ) : (
                      <>
                        <input
                          value={shown}
                          onChange={e => setPerDeviceFaults(m => ({ ...m, [d.id]: e.target.value }))}
                          placeholder="Describe this unit's fault…"
                          aria-label={'Fault for ' + d.serial}
                          style={{ ...fieldStyle, height: 30, fontSize: 12 }}
                        />
                        {rec?.category && (
                          <div style={{ fontSize: 11, color: recTone.text, marginTop: 3 }}>
                            {perDeviceFaults[d.id] && perDeviceFaults[d.id] !== rec.category
                              ? 'overrides ' + rec.category + ' from ' + rec.sourceLabel
                              : 'from ' + rec.sourceLabel}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: wTone.text }}>{w.text}</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => setUnitIds(ids => ids.filter(x => x !== d.id))} className="btn btn-sm btn-danger">Remove</button>
                  </div>
                </div>
              );
            })}
          </StepCard>

          {/* Step 3 — shipment */}
          <StepCard n={3} title="Shipment" sub="Tracking can be added later — the RMA will sit in Awaiting dispatch." clip={false}>
            <div style={{ padding: '16px 18px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Carrier</div>
                <FilterSelect
                  value={carrier} onChange={setCarrier} darkMode={darkMode} OW={OW} DM={DM}
                  options={[{ value: '', label: 'Not set' }, ...(settings?.carriers || []).map(c => ({ value: c, label: c }))]}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Tracking number</div>
                <input
                  value={tracking} onChange={e => setTracking(e.target.value)}
                  placeholder="e.g. 1Z999AA10123456784"
                  style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Dispatch date</div>
                {/* DatePicker mimics a native input and calls back with {target:{value}},
                    so the handler has to unwrap it rather than being passed the setter. */}
                {DatePicker
                  ? <DatePicker value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} darkMode={darkMode} OW={OW} DM={DM} />
                  : <input type="date" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} style={fieldStyle} />}
              </div>
            </div>
          </StepCard>
        </div>

        {/* Sticky summary */}
        <div style={{ position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', padding: '16px 18px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Summary</div>
            {/* Reflects what will actually be filed, not just the chip: with faults read off
                the records the Step 1 field is often empty while every unit has a fault. */}
            <FactRow
              label="Fault"
              value={summaryFault.text}
              valueColor={summaryFault.unset ? 'var(--text-ghost)' : undefined}
            />
            <FactRow label="From record" value={anyRecorded ? (units.length - uncovered.length) + ' of ' + units.length : 'None'} />
            <FactRow label="Mode" value={faultMode === 'single' ? 'One fault for all' : 'Per device'} />
            <FactRow label="Units" value={String(units.length)} mono />
            <FactRow
              label="Out of warranty"
              value={outOfWarranty.length === 0 ? 'None' : String(outOfWarranty.length)}
              valueColor={outOfWarranty.length ? 'var(--danger-text)' : 'var(--success-text)'}
            />
            <FactRow label="Manufacturer" value={settings?.manufacturer || '—'} />
            <FactRow label="Tracking" value={tracking.trim() || 'Not set'} mono={!!tracking.trim()} valueColor={tracking.trim() ? undefined : 'var(--text-ghost)'} last />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              {/* In edit mode there is no dispatch button: dispatching is the detail screen's
                  job, where the carrier and tracking are confirmed against the parcel that
                  is actually going out. Editing only ever saves. */}
              {!editing && (
                <button
                  onClick={() => submit(true)}
                  disabled={!canCreate}
                  className="btn btn-primary btn-lg"
                  style={{ width: '100%', opacity: canCreate ? 1 : 0.45 }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                  Create &amp; dispatch
                </button>
              )}
              <button
                onClick={() => submit(false)}
                disabled={!canCreate}
                className={editing ? 'btn btn-primary btn-lg' : 'btn btn-ghost'}
                style={{ width: '100%', opacity: canCreate ? 1 : 0.45 }}
              >{editing ? 'Save changes' : 'Save without dispatching'}</button>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-ghost)', lineHeight: 1.5, marginTop: 12 }}>
              {units.length === 0
                ? 'Scan at least one unit to raise this RMA.'
                : !canCreate
                  ? (uncovered.length === units.length
                      ? (faultMode === 'single' ? 'Describe the fault to continue.' : 'Give every unit a fault to continue.')
                      : 'Give the remaining ' + uncovered.length + ' unit' + (uncovered.length === 1 ? '' : 's')
                        + ' a fault to continue.')
                  : editing
                    ? 'Saving keeps ' + (editing.ref || 'this RMA') + ' in awaiting dispatch. You can come back and change it again until it ships.'
                    : 'Saved without dispatching, this RMA sits in awaiting dispatch — you can reopen it to add or remove units until it ships.'}
            </div>
          </div>

          <Callout
            tone={outOfWarranty.length ? 'warning' : 'info'}
            title="Warranty check"
          >
            {units.length === 0
              ? 'Scan units to see their warranty position.'
              : outOfWarranty.length === 0
                ? 'All ' + units.length + ' unit' + (units.length === 1 ? '' : 's') + ' in warranty. Nothing on this batch should be chargeable.'
                : outOfWarranty.length + ' of ' + units.length + ' units are out of warranty. Those units may be charged for repair.'}
            {unknownWarranty.length > 0 && (
              ' ' + unknownWarranty.length + ' unit' + (unknownWarranty.length === 1 ? ' has' : 's have')
              + ' no purchase date recorded, so warranty cannot be confirmed.'
            )}
          </Callout>
        </div>
      </div>
    </div>
  );
}

export default RmaNew;
